import { useState, useEffect, useCallback, useRef } from 'react';
import { StatsForNerds } from '../types/stats';
import axios from "axios";
import { getHeadersForRequest } from '../utils/stats';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';

// const API_BASE = import.meta.env.VITE_API_BASE || '/api';
// const API_TOKEN = import.meta.env.VITE_API_TOKEN;
// const MOCK_MODE = import.meta.env.VITE_MOCK_STATS === 'true';

const MOCK_MODE = false;

interface UseStatsForNerdsOptions {
  refreshMs?: number;
  paused?: boolean;
}

interface UseStatsForNerdsReturn {
  data: StatsForNerds | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  paused: boolean;
  setPaused: (paused: boolean) => void;
  history: StatsForNerds[]; // Last 10 polls for sparklines
}

export function useStatsForNerds({
  refreshMs = 10000000,
  paused: initialPaused = false
}: UseStatsForNerdsOptions = {}): UseStatsForNerdsReturn {
  const [data, setData] = useState<StatsForNerds | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(initialPaused);
  const [history, setHistory] = useState<StatsForNerds[]>([]);
  
  const abortControllerRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const navigate = useNavigate();
  const toast = useToast();

  const fetchStats = useCallback(async (): Promise<StatsForNerds> => {
    if (MOCK_MODE) {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
      // return generateMockData();
    }

    // Abort previous request if still running
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    

    const headers = await getHeadersForRequest();

    const response = await axios.get(`${process.env.REACT_APP_TEST_API_URL}/stats/statsForNerds`, {
      headers
    });

    console.log(response);

    if (response.data.status === 401) {
      // throw new Error(`HTTP ${response.status}: ${response.message}`);
      navigate('/');
      toast({ title: "Session Expired", status: "error" });
    }

    return response.data;
  }, []);

  const refresh = useCallback(async () => {
    if (paused) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const newData = await fetchStats();
      setData(newData);
      
      // Update history (keep last 10)
      setHistory(prev => {
        const updated = [...prev, newData];
        return updated.slice(-10);
      });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Request was aborted, ignore
      }
      
      console.error('Failed to fetch stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  }, [fetchStats, paused]);

  // Initial fetch
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Setup interval
  useEffect(() => {
    if (paused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(refresh, refreshMs);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refresh, refreshMs, paused]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    refresh,
    paused,
    setPaused,
    history
  };
}
